import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle2, Plus, Layers, CheckCircle } from 'lucide-react';
import { useCreatePlybook, useUpdatePlybook, usePlybookPlies, useDeletePly } from '@/hooks/usePlybooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { cn } from '@/lib/utils';
import { PlyEditor } from './PlyEditor';
import { PlyCard } from './PlyCard';
import type { Plybook, PlybookPly } from '@/types/plybook';

const schema = z.object({
  name: z.string().min(2, 'Minimo 2 caracteres'),
  code: z.string().optional(),
  description: z.string().optional(),
  customer_id: z.string().optional(),
  project_id: z.string().optional(),
  product_model_id: z.string().optional(),
  mold_id: z.string().optional(),
  version: z.string().default('1.0'),
  notes: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  plybook?: Plybook | null;
  defaultMoldId?: string;
  defaultCustomerId?: string;
  defaultProjectId?: string;
  defaultName?: string;
  defaultCode?: string;
  defaultVersion?: string;
  hideProductField?: boolean;
  // En modo alta recibe el plybook recién creado (para autoseleccionarlo, etc.)
  onSuccess?: (created?: Plybook) => void;
}

function FI({ label, name, required, reg, err, touched, type = 'text', ph }: any) {
  const e = err[name]; const ok = touched[name] && !e;
  return (<div className="space-y-1.5"><Label className={cn(e && 'text-destructive')}>{label}{required && <span className="text-destructive ml-1">*</span>}</Label><div className="relative"><Input type={type} placeholder={ph} {...reg(name)} className={cn(e && 'border-destructive', ok && 'border-green-500')} />{ok && <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}</div>{e && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" />{e.message}</p>}</div>);
}

export function PlybookFormDialog({ open, onOpenChange, plybook, defaultMoldId, defaultCustomerId, defaultProjectId, defaultName, defaultCode, defaultVersion, hideProductField, onSuccess }: Props) {
  const isEditing = !!plybook;
  const createMutation = useCreatePlybook();
  const updateMutation = useUpdatePlybook();

  // Paso 2 (solo en alta): tras crear la cabecera, el diálogo pasa a la vista
  // de capas para que el usuario pueda agregarlas sin salir del flujo.
  const [createdPlybook, setCreatedPlybook] = useState<Plybook | null>(null);
  const [plyEditorOpen, setPlyEditorOpen] = useState(false);
  const [editingPly, setEditingPly] = useState<PlybookPly | null>(null);
  const [showAddZone, setShowAddZone] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneColor, setNewZoneColor] = useState('#ec4899');
  const queryClient = useQueryClient();
  
  // Query zones
  const { data: zones } = useQuery({
    queryKey: ['plybook-zones'],
    queryFn: async () => {
      const { data, error } = await api
        .from('plybook_zones')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
  });
  
  // Add zone mutation
  const addZoneMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const maxOrder = zones?.length ? Math.max(...zones.map((z: any) => z.sort_order)) : 0;
      const { data, error } = await api
        .from('plybook_zones')
        .insert([{ name, color, sort_order: maxOrder + 1 }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plybook-zones'] });
      setShowAddZone(false);
      setNewZoneName('');
      setNewZoneColor('#ec4899');
    },
  });
  const { data: createdPlies } = usePlybookPlies(createdPlybook?.id);
  const deletePlyMutation = useDeletePly();
  const nextPlyNumber = (createdPlies || []).reduce((max: number, p: any) => Math.max(max, p.ply_number || 0), 0) + 1;

  const handleDialogOpenChange = (o: boolean) => {
    if (!o && createdPlybook) {
      // Cerrar desde el paso de capas (Terminar o X): el plybook ya existe
      onSuccess?.(createdPlybook);
      setCreatedPlybook(null);
      setPlyEditorOpen(false);
      setEditingPly(null);
    }
    onOpenChange(o);
  };

  const { register, handleSubmit, setValue, setError, watch, reset, formState: { errors, touchedFields, isSubmitting, isValid, isDirty } } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: { name: '', code: defaultCode || '', description: '', customer_id: defaultCustomerId || '', project_id: defaultProjectId || '', product_model_id: '', mold_id: defaultMoldId || '', version: defaultVersion || '1.0', notes: '' },
  });

  // Watch para filtrado dinámico
  const cid = watch('customer_id');
  const pid = watch('project_id');

  const { data: customers } = useQuery({ queryKey: ['customers-plybook'], queryFn: async () => { const { data } = await api.from('customers').select('id,name,code').eq('is_active', true).order('name'); return data || []; } });
  const { data: projects } = useQuery({ queryKey: ['projects-plybook'], queryFn: async () => { const { data } = await api.from('projects').select('id,name,reference_code,customer_id').eq('is_active', true).order('name'); return data || []; } });
  const { data: molds } = useQuery({ queryKey: ['molds-plybook', cid], queryFn: async () => { let q = api.from('molds').select('id,code,name').order('code'); if (cid) q = q.eq('customer_id', cid); const { data } = await q; return data || []; } });
  const { data: products } = useQuery({ queryKey: ['products-plybook', cid, pid], queryFn: async () => { let q = api.from('product_models').select('id,code,name').order('code'); if (cid) q = q.eq('customer_id', cid); if (pid) q = q.eq('project_id', pid); const { data } = await q; return data || []; } });

  useEffect(() => {
    if (!open) return;
    setCreatedPlybook(null);
    setPlyEditorOpen(false);
    setEditingPly(null);
    if (plybook) {
      reset({ name: plybook.name || '', code: plybook.code || '', description: plybook.description || '', customer_id: plybook.customer_id || '', project_id: plybook.project_id || '', product_model_id: plybook.product_model_id || '', mold_id: plybook.mold_id || '', version: plybook.version || '1.0', notes: plybook.notes || '' });
    } else {
      reset({ name: defaultName || '', code: defaultCode || '', description: '', customer_id: defaultCustomerId || '', project_id: defaultProjectId || '', product_model_id: '', mold_id: defaultMoldId || '', version: defaultVersion || '1.0', notes: '' });
    }
  }, [open, plybook, reset, defaultMoldId, defaultCustomerId, defaultProjectId, defaultName, defaultCode, defaultVersion]);

  const onSubmit = async (data: FormData) => {
    const clean = (v: string | undefined) => v && v !== '__none__' ? v : null;
    const payload = { name: data.name.trim(), code: data.code?.trim() || null, description: data.description?.trim() || null, customer_id: clean(data.customer_id), project_id: clean(data.project_id), product_model_id: clean(data.product_model_id), mold_id: clean(data.mold_id), version: data.version || '1.0', notes: data.notes?.trim() || null };

    // El código tiene constraint único en BD y los plybooks eliminados solo se
    // desactivan (is_active=false): siguen ocupando su código aunque no se vean
    // en la lista. Chequeo previo para dar un mensaje útil en el propio campo.
    if (payload.code) {
      const { data: existing } = await api
        .from('plybooks')
        .select('id,name,is_active')
        .eq('code', payload.code)
        .limit(1);
      const dup = (existing || [])[0];
      if (dup && (!isEditing || dup.id !== plybook?.id)) {
        setError('code', {
          type: 'manual',
          message: `Código en uso por «${dup.name}»${dup.is_active ? '' : ' (archivado)'}. Prueba p.ej. ${payload.code}-V2`,
        });
        return;
      }
    }

    try {
      if (isEditing && plybook) {
        await updateMutation.mutateAsync({ id: plybook.id, ...payload });
      } else {
        // Alta: no cerrar; pasar al paso de capas con el plybook recién creado
        const created = await createMutation.mutateAsync(payload);
        setCreatedPlybook(created as Plybook);
        return;
      }
    } catch {
      // El toast de error ya lo muestra la mutación; mantenemos el diálogo abierto.
      return;
    }
    onOpenChange(false);
    onSuccess?.();
  };

  const fp = projects?.filter((p: any) => !cid || p.customer_id === cid) || [];
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Paso 2: gestión de capas del plybook recién creado
  if (createdPlybook) {
    return (
      <>
        <Dialog open={open} onOpenChange={handleDialogOpenChange}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                Capas de {createdPlybook.name}
                {createdPlybook.code && <Badge variant="outline" className="font-mono">{createdPlybook.code}</Badge>}
              </DialogTitle>
            </DialogHeader>

            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              Plybook creado. Agrega ahora las capas de laminado.
            </div>

            {/* Zone Management */}
            <div className="border rounded-lg p-3 bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">Zonas del Molde</h4>
                {!showAddZone && (
                  <Button size="sm" variant="outline" onClick={() => setShowAddZone(true)}>
                    <Plus className="w-3 h-3 mr-1" /> Nueva Zona
                  </Button>
                )}
              </div>
              
              {showAddZone && (
                <div className="mb-3 p-2 border rounded bg-background">
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newZoneName}
                      onChange={(e) => setNewZoneName(e.target.value)}
                      placeholder="Nombre de la zona"
                      className="flex-1"
                    />
                    <input
                      type="color"
                      value={newZoneColor}
                      onChange={(e) => setNewZoneColor(e.target.value)}
                      className="w-10 h-9 rounded border cursor-pointer"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowAddZone(false)}>
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => addZoneMutation.mutate({ name: newZoneName, color: newZoneColor })}
                      disabled={!newZoneName.trim() || addZoneMutation.isPending}
                    >
                      Guardar
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap gap-2">
                {zones?.map((zone: any) => (
                  <div
                    key={zone.id}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: zone.color }}
                  >
                    {zone.name}
                  </div>
                ))}
                {(!zones || zones.length === 0) && !showAddZone && (
                  <span className="text-xs text-muted-foreground">Sin zonas definidas</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {(createdPlies || []).length === 0 ? (
                <div className="border border-dashed rounded-lg p-6 text-center text-sm text-muted-foreground">
                  Sin capas todavía. Pulsa "Agregar capa" para crear la primera.
                </div>
              ) : (
                (createdPlies || []).map((ply: any) => (
                  <PlyCard
                    key={ply.id}
                    ply={ply}
                    onEdit={() => { setEditingPly(ply); setPlyEditorOpen(true); }}
                    onDelete={() => deletePlyMutation.mutate({ id: ply.id, plybookId: createdPlybook.id })}
                  />
                ))
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => { setEditingPly(null); setPlyEditorOpen(true); }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar capa
            </Button>

            <DialogFooter>
              <Button type="button" onClick={() => handleDialogOpenChange(false)}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Terminar ({(createdPlies || []).length} capa{(createdPlies || []).length === 1 ? '' : 's'})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PlyEditor
          open={plyEditorOpen}
          onOpenChange={setPlyEditorOpen}
          plybookId={createdPlybook.id}
          ply={editingPly}
          nextPlyNumber={nextPlyNumber}
        />
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEditing ? 'Editar Plybook' : 'Nuevo Plybook'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
            <h4 className="text-sm font-medium text-muted-foreground">Datos Basicos</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <FI label="Nombre" name="name" required reg={register} err={errors} touched={touchedFields} />
              <FI label="Codigo" name="code" reg={register} err={errors} touched={touchedFields} />
            </div>
            <FI label="Version" name="version" reg={register} err={errors} touched={touchedFields} />
          </div>

          <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
            <h4 className="text-sm font-medium text-muted-foreground">Relaciones</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Cliente</Label><SearchableSelect options={(customers||[]).map((c:any)=>({value:c.id,label:c.name||"Sin nombre",description:c.code||""}))} value={watch('customer_id')||''} onValueChange={v=>setValue('customer_id',v,{shouldDirty:true})} placeholder="Seleccionar..." allowClear /></div>
              <div className="space-y-1.5"><Label>Proyecto</Label><SearchableSelect options={fp.map((p:any)=>({value:p.id,label:p.name||"Sin nombre"}))} value={watch('project_id')||''} onValueChange={v=>setValue('project_id',v,{shouldDirty:true})} placeholder="Seleccionar..." allowClear /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Molde</Label><SearchableSelect options={(molds||[]).map((m:any)=>({value:m.id,label:m.code||"Sin codigo",description:m.name||""}))} value={watch('mold_id')||''} onValueChange={v=>setValue('mold_id',v,{shouldDirty:true})} placeholder="Seleccionar..." allowClear /></div>
              {!hideProductField && <div className="space-y-1.5"><Label>Producto</Label><SearchableSelect options={(products||[]).map((p:any)=>({value:p.id,label:p.code||"Sin codigo",description:p.name||""}))} value={watch('product_model_id')||''} onValueChange={v=>setValue('product_model_id',v,{shouldDirty:true})} placeholder="Seleccionar..." allowClear /></div>}
            </div>
          </div>

          <div className="space-y-1.5"><Label>Descripcion</Label><Textarea {...register('description')} rows={2} /></div>
          <div className="space-y-1.5"><Label>Notas</Label><Textarea {...register('notes')} rows={2} /></div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isPending||!isValid||!isDirty}>{isPending?<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Guardando...</>:isEditing?'Guardar':'Crear'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}